import _ from 'lodash';
import * as React from 'react';
import {AppState, InteractionManager, Linking, Platform} from 'react-native';
import {connect} from 'react-redux';
import ActionsFactory from 'src/actions/ActionsFactory';
import NavActions from 'src/actions/NavActions';
import AppRoutes from 'src/AppRoutes';
import Localized from 'src/constants/AppStrings';
import BuildTypes from 'src/constants/BuildTypeConstants';
import CartTypes from 'src/constants/cart/CartTypes';
import {authStore} from 'src/init';
import Events from 'src/logging/Events';
import {CreditCard} from 'src/models/CreditCard';
import LocationFeatures from 'src/models/LocationFeatures';
import {PaymentCredentials} from 'src/models/PaymentCredentials';
import OneSignal from 'src/nativeModules/OneSignal';
import PassKit from 'src/nativeModules/PassKit';
import TabNavigator from 'src/navigators/TabNavigator';
import MainConsumerProvider from 'src/providers/mainConsumer';
import {RootState} from 'src/redux/store';
import MessageRepository from 'src/services/aws/MessageRepository';
import PersistentStore from 'src/services/PersistentStoreService';
import Settings from 'src/Settings';
import AccountStore, {UrlTypes} from 'src/stores/AccountStore';
import MachineStore from 'src/stores/MachineStore';
import type {DiscountItem, PromotionType} from 'src/types/Promotions';
import type {RewardType} from 'src/types/Rewards';
import Util from 'src/Util';
import DeepLinkHandler from '../elements/DeepLinkHandler';
import {alertError, alertSuccess, confirm} from '../helpers/AlertHelper';
import ScreenContext from '../ScreenContext';
import LoadingScreen from './LoadingScreen';

const DISCOUNT_AMOUNT_TYPE = 2;
type MainConsumerScreenProps = {
  navigation: any;
  paymentCredentials: PaymentCredentials;
  creditCards: Array<CreditCard>;
  balance: number;
  rewards: number;
};
type MainConsumerScreenState = {
  offline: boolean;
  balance: number;
  rewards: number;
  email: string;
  fullName: string;
  canAddPass: boolean;
  passUrl: string;
  passExists: boolean;
  taxId: string;
  showEatItOrDeleteIt: boolean;
  machinesCount: number;
  messagesCount: number;
  loaded: boolean;
  rewardSteps: Array<RewardType>;
};

class MainConsumerScreen extends React.Component<
  MainConsumerScreenProps,
  MainConsumerScreenState
> {
  currentPrivacyVersion: string;
  currentPrivacyType: string;
  handleAppStateChangeSubscription: any;

  static getGmaApiBase() {
    return AccountStore.getUrl(UrlTypes.gmaapi);
  }

  static contextType = ScreenContext;

  constructor(props: MainConsumerScreenProps) {
    super(props);
    this.state = {
      offline: false,
      loaded: false,
      balance: props.balance,
      rewards: props.rewards,
      fullName: AccountStore.getFullName(),
      email: AccountStore.getEmail(),
      taxId: AccountStore.getTaxId(),
      canAddPass: false,
      passExists: true,
      passUrl: `${MainConsumerScreen.getGmaApiBase()}/passkit/v1/pass/${AccountStore.getAccountId()}`,
      showEatItOrDeleteIt: this.showEatItOrDeleteIt(),
      machinesCount: MachineStore.getShops().length,
      messagesCount: 0,
      rewardSteps: this.getRewardSteps(),
    };
    this.onPrivacyConfirm = this.onPrivacyConfirm.bind(this);
    this.onPrivacyDecline = this.onPrivacyDecline.bind(this);
    this.onCloseAccount = this.onCloseAccount.bind(this);
    this.showEatItOrDeleteIt = this.showEatItOrDeleteIt.bind(this);
    this.onPromotionSelected = this.onPromotionSelected.bind(this);
    this.getPromotionDescription = this.getPromotionDescription.bind(this);
    this.getScanScreenParams = this.getScanScreenParams.bind(this);
    this._onAccountChange = this._onAccountChange.bind(this);
    this._onBalanceChange = this._onBalanceChange.bind(this);
    this.onMessagesChanged = this.onMessagesChanged.bind(this);
    this.onMachineStoreChange = this.onMachineStoreChange.bind(this);
    this.passAdded = this.passAdded.bind(this);
    this._handleAppStateChange = this._handleAppStateChange.bind(this);
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    if (nextProps.balance !== prevState.balance) {
      return {
        balance: nextProps.balance,
        rewards: nextProps.rewards,
      };
    }
    return prevState;
  }

  async componentDidMount() {
    if (!(await authStore.getRefreshToken())) {
      await ActionsFactory.getAccountActions().logout();
      this.context.actions.changeRootRoute(AppRoutes.Login);
      return;
    }

    if (
      Settings.buildType !== BuildTypes.pantry &&
      Settings.buildType !== BuildTypes.social
    ) {
      const result = await PassKit.canAddPass();
      const numUnreadMessages = await MessageRepository.getNumUnreadMessages();

      let passExistsResult = true;

      if (result) {
        passExistsResult = await PassKit.doesPassExist(this.state.passUrl);
      }

      this.setState({
        passExists: passExistsResult,
        canAddPass: result,
        messagesCount: numUnreadMessages,
      });
    }

    MessageRepository.addMessageListener(this.onMessagesChanged);
    MachineStore.addChangeListener(this.onMachineStoreChange);
    this.setState({
      loaded: true,
    });
    AccountStore.addChangeListener(this._onAccountChange);
    AccountStore.addBalanceChangedListener(this._onBalanceChange);

    this._onAccountChange();

    this._onBalanceChange();

    if (Platform.OS === 'web') {
      OneSignal.promptForPushNotificationPermissions((result) => {});
      AccountStore.waitForData().then(() =>
        OneSignal.getTags((tags) => {
          if (
            !AccountStore.isDemo() &&
            (!tags || tags.mka !== AccountStore.getAccountId())
          ) {
            OneSignal.sendTags({
              email: AccountStore.getEmail(),
              mka: AccountStore.getAccountId(),
              org: AccountStore.getOrg(),
              location: AccountStore.getLocationId(),
              region: AccountStore.getRegion(),
              city: AccountStore.getCity(),
              zip: AccountStore.getZip(),
              currency: AccountStore.getCurrency(),
              env: AccountStore.getEnvironment(),
            });
            OneSignal.setExternalUserId(AccountStore.getAccountId());
          }
        }),
      );
    }

    InteractionManager.runAfterInteractions(async () => {
      try {
        const accountResponse: any = await ActionsFactory.getAccountActions().reloadConsumerData(
          {
            accountId: AccountStore.getAccountId(),
            accountBalanceId: AccountStore.getAccountBalanceId(),
            email: AccountStore.getEmail(),
            userInitiated: true,
          },
        );

        if (accountResponse && accountResponse.status === 'ok') {
          if (accountResponse.data.currentprivacyversion) {
            this.currentPrivacyVersion =
              accountResponse.data.currentprivacyversion;
            this.currentPrivacyType = accountResponse.data.currentprivacytype;
            NavActions.replace(AppRoutes.PrivacyPolicy, {
              title: Localized.Labels.updated_privacy_policy,
              onDecline: this.onPrivacyDecline,
              onConfirm: this.onPrivacyConfirm,
              version: this.currentPrivacyVersion,
              locale: accountResponse.data.locale,
              type: this.currentPrivacyType,
            });
          }

          const email = AccountStore.getEmail();
          const hasSeenPayrollMessage = await PersistentStore.hasSeenPayrollDeductAvailableMessage(
            email,
          );

          if (
            !accountResponse.data.currentprivacyversion &&
            accountResponse.data.showPayroll &&
            !hasSeenPayrollMessage
          ) {
            PersistentStore.setSeenPayrollDeductAvailableMessage(email);
            confirm(
              Localized.Labels.would_you_like_to_manage_payroll_deduct,
              this.navigateToPayrollDeduct,
              undefined,
              Localized.Labels.payroll_deduct_now_available,
            );
          }

          const hasSentExternalId = await PersistentStore.hasSentExternalId();

          if (!hasSentExternalId) {
            OneSignal.setExternalUserId(AccountStore.getAccountId());
            PersistentStore.setSentExternalId();
          }

          if (Platform.OS !== 'android') {
            Events.AppleWallet.trackEvent('AppleWallet:Usage', {
              canUse: this.props.paymentCredentials.applePayAvailable
                ? 'Yes'
                : 'No',
            });
          }
        }
      } catch {
        this.setState({
          offline: true,
        });
      }
    });
    this.handleAppStateChangeSubscription = AppState.addEventListener(
      'change',
      this._handleAppStateChange,
    );
  }

  async componentWillUnmount() {
    AccountStore.removeChangeListener(this._onAccountChange);
    AccountStore.removeBalanceChangedListener(this._onBalanceChange);
    MachineStore.removeChangeListener(this.onMachineStoreChange);
    this.handleAppStateChangeSubscription?.remove();
    MessageRepository.removeMessageListener(this.onMessagesChanged);
  }

  _handleAppStateChange(currentAppState) {
    if (currentAppState === 'active' && AccountStore.getAccountId()) {
      ActionsFactory.getAccountActions().reloadConsumerData({
        accountId: AccountStore.getAccountId(),
        accountBalanceId: AccountStore.getAccountBalanceId(),
        email: AccountStore.getEmail(),
        userInitiated: true,
      });
    }
  }

  navigateToPayrollDeduct() {
    NavActions.nestedPush(
      [AppRoutes.Main, AppRoutes.Settings],
      AppRoutes.PayrollDeduct,
    );
  }

  passAdded() {
    this.setState({
      passExists: true,
    });
  }

  async onMessagesChanged() {
    this.setState({
      messagesCount: await MessageRepository.getNumUnreadMessages(),
    });
  }

  onMachineStoreChange() {
    const shops = MachineStore.getShops();
    this.setState({
      machinesCount: shops.length,
    });
  }

  getRewardSteps() {
    const thresholdResponse = AccountStore.getThresholdsResponse();
    let rewardSteps = [];

    if (thresholdResponse?.status === 'ok') {
      rewardSteps = thresholdResponse.data;
    }

    return rewardSteps;
  }

  _onAccountChange() {
    const newName = AccountStore.getFullName();
    const newEmail = AccountStore.getEmail();
    const newTaxId = AccountStore.getTaxId();
    const showEatIt = this.showEatItOrDeleteIt();
    const rewardSteps = this.getRewardSteps();

    if (
      newName !== this.state.fullName ||
      newEmail !== this.state.email ||
      newTaxId !== this.state.taxId ||
      showEatIt !== this.state.showEatItOrDeleteIt ||
      !_.isEqual(rewardSteps, this.state.rewardSteps)
    ) {
      this.setState({
        fullName: newName,
        email: newEmail,
        taxId: newTaxId,
        showEatItOrDeleteIt: showEatIt,
        rewardSteps,
      });
    }
    const accountId = AccountStore.getAccountId();
    if (accountId === '-1') {
      this.context.actions.changeRootRoute(AppRoutes.Login);
    }
  }

  showEatItOrDeleteIt() {
    return (
      AccountStore.getShowEatItOrDeleteIt() &&
      Settings.buildType === BuildTypes.canteen
    );
  }

  _onBalanceChange() {
    const newBalance = AccountStore.getAccountBalance();
    const newRewards = AccountStore.getAccountRewards();

    if (
      this.state.balance !== newBalance ||
      this.state.rewards !== newRewards
    ) {
      this.setState({
        balance: newBalance,
        rewards: newRewards,
      });
    }
  }

  getScanScreenParams(machine: any, promotionDescription = '') {
    return {
      marketName: machine.name,
      locationId: machine.deviceId.toString(),
      locationType: machine.locationType,
      balance: this.state.balance,
      promotionDescription,
      beaconId: machine.broadcastid,
    };
  }

  getPromotionSavingsDescription(promotion: PromotionType) {
    if (promotion.type === DISCOUNT_AMOUNT_TYPE) {
      return Localized.Labels.formatString(
        Localized.Labels.promotion_description_savings,
        Util.formatCurrency(
          this.props,
          promotion.amount,
          AccountStore.getCurrency(),
        ),
      );
    } else {
      return Localized.Labels.formatString(
        Localized.Labels.promotion_description_savings,
        `${promotion.percentage}%`,
      );
    }
  }

  getPromotionMinDescription(promotion: PromotionType) {
    return Localized.Labels.formatString(
      Localized.Labels.promotion_description_minimum,
      Util.formatCurrency(
        this.props,
        promotion.transactionMinimum,
        AccountStore.getCurrency(),
      ),
    );
  }

  getPromotionLimitDescription(promotion: PromotionType) {
    return `\n${Localized.Labels.formatString(
      Localized.Labels.promotion_description_limit,
      promotion.score,
    )}`;
  }

  getPromotionItemText(items: Array<DiscountItem>) {
    const description = [];

    if (items) {
      description.push(this.getItemText(items[0]));
      items.slice(1).forEach((item) => {
        description.push(Localized.Labels.or);
        description.push(this.getItemText(item));
      });
    }

    return description;
  }

  getItemText(item: DiscountItem): string {
    if (item.category) {
      return item.category;
    }

    return item.item;
  }

  async onPromotionSelected(
    promotion: PromotionType,
    discountItems: Array<DiscountItem>,
  ) {
    if (promotion.video) {
      Linking.openURL('https://visa.com/NFLUltimateFanExperienceFullRules');
    } else if (Platform.OS === 'web') {
      alertSuccess(
        Localized.Errors.promotion_only_available_market,
        () => {},
        Localized.Errors.sorry,
      );
    } else {
      const locationId = AccountStore.getLocationId();

      if (locationId) {
        try {
          const location = await ActionsFactory.getAccountActions().getLocation(
            locationId,
          );
          const locationFeatures = new LocationFeatures(location.data.features);

          if (!locationFeatures.HasScanAndPay) {
            alertError(Localized.Errors.discount_available_at_kiosk);
          } else if (!MachineStore.isLocationInList(locationId)) {
            alertError(
              Localized.Errors.you_must_be_within_range_to_redeem_discount,
              null,
              () => {},
              '',
            );
          } else {
            const params = this.getScanScreenParams(
              {
                name: AccountStore.getLocationName(),
                deviceId: locationId,
                locationType: AccountStore.getLocationType(),
                broadcastid:
                  AccountStore.getLocationBroadcastId() || locationId,
              },
              this.getPromotionDescription(promotion, discountItems),
            );
            NavActions.navigate(AppRoutes.Scan, {
              ...params,
              cartType: CartTypes.ScanAndPay,
            });
          }
        } catch (error) {
          Events.Error.trackEvent(
            'Exception',
            'MainConsumerScreen:onPromotionSelected',
            error.message ? error.message : error.toString(),
          );
        }
      }
    }
  }

  getPromotionDescription(
    promotion: PromotionType,
    discountItems: Array<DiscountItem>,
  ): string {
    let description: any = [Localized.Labels.add_a];
    const items = discountItems.filter(
      (item) => item.discountHeaderId === promotion.discountHeaderId,
    );
    description = description.concat(this.getPromotionItemText(items));
    description.push(this.getPromotionSavingsDescription(promotion));

    if (promotion.transactionMinimum > 0) {
      description.push(this.getPromotionMinDescription(promotion));
    }

    if (promotion.score > 0) {
      description.push(this.getPromotionLimitDescription(promotion));
    }

    return description.join(' ');
  }

  onPrivacyConfirm() {
    ActionsFactory.getAccountActions().acceptPrivacy(
      AccountStore.getAccountId(),
      this.currentPrivacyVersion,
      Util.getCurrentDate(),
    );
    NavActions.replace(AppRoutes.Main);
  }

  async onCloseAccount() {
    ActionsFactory.getAccountActions().deleteAccount(
      AccountStore.getAccountId(),
      AccountStore.getEmail(),
    );
    // Need to navigate away from the privacy-policy screen
    // because it's in the LoginNavigator as well.
    NavActions.replace(AppRoutes.MyModal);
    await AccountStore.logout();
    this.context.actions.changeRootRoute(AppRoutes.Login);
  }

  onPrivacyDecline() {
    confirm(
      Localized.Labels.declining_privacy_policy,
      this.onCloseAccount,
      undefined,
      undefined,
      Localized.Buttons.cancel,
      Localized.Buttons.confirm,
    );
  }

  render() {
    if (!this.state.loaded) {
      return <LoadingScreen />;
    } else {
      return (
        <MainConsumerProvider
          accountId={AccountStore.getAccountId()}
          oldContext={{
            state: {
              ...this.context.state,
              ...this.state,
              shopBadgeCount: this.state.machinesCount,
              inboxCount: this.state.messagesCount,
            },
            actions: {
              ...this.context.actions,
              promotionSelected: this.onPromotionSelected,
              getScanScreenParams: this.getScanScreenParams,
              passAdded: this.passAdded,
            },
          }}
        >
          <DeepLinkHandler />
          <TabNavigator />
        </MainConsumerProvider>
      );
    }
  }
}

const ConnectedMainConsumerScreen = connect((state: RootState) => ({
  paymentCredentials: state.account.paymentCredentials,
  creditCards: state.account.creditCards,
  balance: state.account.account.defaultBalance?.balance,
  rewards: state.account.account.points,
}))(MainConsumerScreen);

export default ConnectedMainConsumerScreen;
